import React from 'react';
import Select from 'react-select';
import css from './FieldMultiSelect.module.css';
import classNames from 'classnames';
import { IconSpinner } from '..';

/**
 * Default styles for react-select. You can still override via customStyles prop.
 */
const defaultSelectStyles = {
  control: (styles, { isFocused, isDisabled, isSelected }) => ({
    ...styles,
    backgroundColor: '#fff',
    boxShadow: 'none',
    border: isFocused || isSelected ? '1px solid #b0b0b5' : '1px solid #d8dce6',
    color: '#4a4a4a',
    fontSize: '16px',
    lineHeight: '130%',
    letterSpacing: '-0.04em',
    height: '45px',
    minHeight: '45px',
    borderRadius: '6px',
    margin: '0px',
    fontWeight: '400',
    position: 'relative',
    cursor: 'pointer !important',
    '@media (min-width: 1024px)': { fontSize: '16px' },
    '@media (min-width: 1280px)': { fontSize: '16px' },
    ':hover': { border: '1px solid #b0b0b5' },
  }),
  valueContainer: (styles, { isSelected }) => ({
    ...styles,
    color: isSelected ? '#4a4a4a' : '#B2B2B2',
    height: '100%',
    padding: '0px 16px',
    margin: '0px',
    fontWeight: '400',
  }),
  placeholder: styles => ({
    ...styles,
    color: '#4a4a4a',
    padding: '0px',
    margin: '0px',
    fontWeight: '400',
  }),
  indicatorsContainer: styles => ({
    ...styles,
    padding: '0px',
    margin: '0px 10px 0 0',
  }),
  dropdownIndicator: (base, state) => ({
    ...base,
    padding: '0px',
    margin: '0px',
    transition: 'all .2s ease',
    transform: state.isFocused.menuIsOpen ? 'rotate(180deg)' : null,
  }),
  input: (styles, { isSelected }) => ({
    ...styles,
    color: isSelected ? '#4a4a4a' : '#B2B2B2',
    height: '100%',
    padding: '0px',
    margin: '0px',
    fontWeight: '400',
  }),
  indicatorSeparator: () => ({ display: 'none', padding: '0px', margin: '0px' }),
  option: (styles, { isFocused, isDisabled, isSelected }) => ({
    ...styles,
    color: '#4a4a4a',
    padding: '10px 15px',
    fontSize: '16px',
    lineHeight: '100%',
    letterSpacing: '-0.04em',
    cursor: 'pointer !important',
    backgroundColor: isDisabled ? null : isFocused ? '#eee' : isSelected ? '#FFF' : null,
    ':hover': {
      backgroundColor: '#eee',
      color: '#4a4a4a',
    },
  }),
  singleValue: styles => ({
    ...styles,
    color: '#4a4a4a',
    fontWeight: '400',
  }),
  menu: base => ({
    ...base,
    borderRadius: '6px',
    marginTop: 0,
    backgroundColor: '#fff',
    color: '#4a4a4a',
    position: 'absolute',
    top: '100%',
    left: '0',
    right: '0',
    width: '100%',
    minWidth: '200px',
    padding: '0px',
  }),
  menuList: base => ({
    ...base,
    padding: '0px',
    backgroundColor: '#fff',
    fontSize: '16px',
    lineHeight: '100%',
    letterSpacing: '-0.04em',
    color: '#4a4a4a',
  }),
  dropdownIndicator: base => ({
    ...base,
    color: '#4a4a4a',
    padding: '0px',
  }),
};

/**
 * FieldMultiSelect: Final Form <Field> component for react-select (single or multi)
 *
 * Accepts either primitives (e.g., 'kg', 'cm') or option objects ({ value, label })
 * in initial values, and normalizes them to the option instances from `options`.
 */
function FieldMultiSelect({
  input,
  options,
  label,
  isMulti = true,
  disabled = false,
  inProgress,
  onChange,
  customStyles = defaultSelectStyles,
  placeholder = 'Select...',
}) {
  const renderSpinner = inProgress ? (
    <span className={css.loader}>
      <IconSpinner />
    </span>
  ) : null;

  // Normalize any incoming value to the actual option objects from `options`
  const toOption = (val, opts) => {
    if (val == null) return null;
    // If already an object with a value key
    if (typeof val === 'object' && 'value' in val) {
      const found = (opts || []).find(o => o.value === val.value);
      return found || val;
    }
    // If primitive (string/number), find matching option
    if (typeof val === 'string' || typeof val === 'number') {
      return (opts || []).find(o => o.value === val) || null;
    }
    return null;
  };

  const toOptionsArray = (val, opts) => {
    if (!Array.isArray(val)) return [];
    return val.map(v => toOption(v, opts)).filter(Boolean);
  };

  const normalizedValue = isMulti
    ? toOptionsArray(input.value || [], options)
    : toOption(input.value, options);

  const handleChange = selectedOptions => {
    // Store option objects in form state (consistently).
    // If you want to store only raw values: map to .value here.
    input.onChange(selectedOptions);
    if (onChange) onChange(selectedOptions);
  };

  return (
    <div className={classNames(css.reactSelect)}>
      {label && (
        <label>
          {label} {renderSpinner}
        </label>
      )}
      <Select
        value={normalizedValue}
        onChange={handleChange}
        options={options}
        isMulti={isMulti}
        placeholder={placeholder}
        styles={customStyles}
        isDisabled={disabled}
        getOptionValue={o => o.value}
        getOptionLabel={o => o.label}
        components={{ IndicatorSeparator: () => null }}
      />
    </div>
  );
}

export default FieldMultiSelect;
